import { PERMISSIONS } from '@helios/authz';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/authContext';
import {
  adminClient, AdminRequestError, type AdminListQuery, type AdminUserActionInput,
  type AdminUserCredentialInput, type AdminUserLifecycleInput, type CreateAdminRoleInput,
  type CreateAdminUserInput, type ProvisionAdminUserInput, type UpdateAdminTenantInput,
  type UpdateAdminUserInput,
} from '@/services/adminClient';
import type { RequestOptions } from '@/services/apiClient';
import { sessionScopeKey, useSessionScopeFence, type SessionScopeIdentity } from './useSessionScope';

export const adminKeys = {
  scope: (kind: string, identity: SessionScopeIdentity, query: unknown = null) =>
    ['admin', kind, ...sessionScopeKey(identity), query] as const,
};
function changed() {
  return new AdminRequestError('Administration access changed. Reload this page before continuing.', 409, 'REQUEST_ORIGIN_CHANGED');
}
function matches(key: readonly unknown[], origin: SessionScopeIdentity) {
  return key[0] === 'admin' && sessionScopeKey(origin).every((part, index) => key[index + 2] === part);
}
function useAdminRead<T>(permission: string, kind: string, parameters: unknown, loader: (options: RequestOptions) => Promise<T>, requested = true) {
  const { identity, enabled, isCurrent } = useSessionScopeFence(permission);
  const query = useQuery({
    queryKey: adminKeys.scope(kind, identity, parameters), enabled: enabled && requested,
    placeholderData: () => undefined, staleTime: 0, retry: false,
    refetchOnMount: 'always', refetchOnWindowFocus: 'always',
    queryFn: async ({ signal }) => {
      if (!enabled || !requested || !isCurrent(identity)) throw changed();
      const result = await loader({ signal, sessionOrigin: identity.sessionOrigin });
      signal.throwIfAborted();
      if (!isCurrent(identity)) throw changed();
      return result;
    },
  });
  return { ...query, data: enabled && requested && !query.isError ? query.data : undefined };
}
export function useAdminUsers(query: AdminListQuery, enabled = true) {
  return useAdminRead(PERMISSIONS.USERS_MANAGE, 'users', query, (options) => adminClient.listUsers(query, options), enabled);
}
export function useAdminUser(id: string | undefined, enabled = true) {
  return useAdminRead(PERMISSIONS.USERS_MANAGE, 'user', id, (options) => adminClient.getUser(id ?? '', options), enabled && Boolean(id));
}
export function useAdminUserHistory(id: string | undefined, query: AdminListQuery, enabled = true) {
  return useAdminRead(PERMISSIONS.USERS_MANAGE, 'user-history', { id, ...query }, (options) => adminClient.listUserHistory(id ?? '', query, options), enabled && Boolean(id));
}
export function useAssignableAdminRoles(query: AdminListQuery & { tenantId: string }, enabled = true) {
  return useAdminRead(PERMISSIONS.USERS_MANAGE, 'assignable-roles', query, (options) => adminClient.listAssignableRoles(query, options), enabled && Boolean(query.tenantId));
}
export function useAdminRoles(query: AdminListQuery, enabled = true) {
  return useAdminRead(PERMISSIONS.ROLES_MANAGE, 'roles', query, (options) => adminClient.listRoles(query, options), enabled);
}
export function useAdminPermissions(enabled = true) {
  return useAdminRead(PERMISSIONS.ROLES_MANAGE, 'permissions', null, adminClient.listPermissions, enabled);
}
export function useAdminTenants(query: AdminListQuery, enabled = true) {
  return useAdminRead(PERMISSIONS.PLATFORM_ADMIN, 'tenants', query, (options) => adminClient.listTenants(query, options), enabled);
}
export function useAdminSessions(query: AdminListQuery, enabled = true) {
  return useAdminRead(PERMISSIONS.USERS_MANAGE, 'sessions', query, (options) => adminClient.listSessions(query, options), enabled);
}

function useAdminMutation<TInput, TOutput>(permission: string, name: string,
  action: (input: TInput, options: RequestOptions) => Promise<TOutput>,
  options: { sensitive?: boolean; targetId?: (input: TInput) => string | undefined; onSuccess?: (data: TOutput, origin: SessionScopeIdentity) => void } = {}) {
  const { identity, enabled, isCurrent } = useSessionScopeFence(permission);
  const client = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['admin-mutation', name, ...sessionScopeKey(identity)],
    retry: false, gcTime: options.sensitive ? 0 : 5 * 60_000,
    onMutate: () => identity,
    mutationFn: async (input: TInput) => {
      if (!enabled || !isCurrent(identity)) throw changed();
      try {
        const result = await action(input, { sessionOrigin: identity.sessionOrigin });
        if (!isCurrent(identity)) throw changed();
        return result;
      } catch (error) {
        if (!isCurrent(identity)) throw changed();
        throw error;
      }
    },
    onSuccess: async (data, input, origin) => {
      if (!origin || !isCurrent(origin)) return;
      options.onSuccess?.(data, origin);
      // A successful self-revocation must reach its caller before sign-out; refetching
      // with the now-revoked session would otherwise obscure the committed receipt.
      if (options.targetId?.(input) === origin.userId) return;
      await client.invalidateQueries({ predicate: (query) => matches(query.queryKey, origin) });
      if (!isCurrent(origin)) throw changed();
    },
  });
  const visible = mutation.context && isCurrent(mutation.context);
  return { ...mutation, data: visible ? mutation.data : undefined,
    error: visible ? mutation.error : null, isPending: Boolean(visible && mutation.isPending) };
}
type Target<T> = { id: string; input: T };
export function useAdminUserMutations() {
  const auth = useAuth();
  const targetId = (value: { id: string }) => value.id;
  return {
    create: useAdminMutation(PERMISSIONS.USERS_MANAGE, 'create-user',
      (input: CreateAdminUserInput, options) => adminClient.createUser(input, options), { sensitive: true }),
    update: useAdminMutation(PERMISSIONS.USERS_MANAGE, 'update-user',
      ({ id, input }: Target<UpdateAdminUserInput>, options) => adminClient.updateUser(id, input, options),
      { targetId, onSuccess: (data, origin) => { if (data.id === origin.userId) auth.applyOwnProfile(origin, data); } }),
    passwordReset: useAdminMutation(PERMISSIONS.USERS_MANAGE, 'password-reset',
      ({ id, input }: Target<AdminUserCredentialInput>, options) => adminClient.resetPassword(id, input, options), { sensitive: true, targetId }),
    provisionAccess: useAdminMutation(PERMISSIONS.USERS_MANAGE, 'provision-access',
      ({ id, input }: Target<ProvisionAdminUserInput>, options) => adminClient.provisionAccess(id, input, options), { sensitive: true, targetId }),
    revokeSessions: useAdminMutation(PERMISSIONS.USERS_MANAGE, 'revoke-sessions',
      ({ id, input }: Target<AdminUserActionInput>, options) => adminClient.revokeSessions(id, input, options), { targetId }),
    remove: useAdminMutation(PERMISSIONS.USERS_MANAGE, 'remove-user',
      ({ id, input }: Target<AdminUserLifecycleInput>, options) => adminClient.removeUser(id, input, options), { targetId }),
    restore: useAdminMutation(PERMISSIONS.USERS_MANAGE, 'restore-user',
      ({ id, input }: Target<AdminUserLifecycleInput>, options) => adminClient.restoreUser(id, input, options), { targetId }),
  };
}
export function useAdminRoleMutations() {
  return {
    create: useAdminMutation(PERMISSIONS.ROLES_MANAGE, 'create-role',
      (input: CreateAdminRoleInput, options) => adminClient.createRole(input, options)),
    updatePermissions: useAdminMutation(PERMISSIONS.ROLES_MANAGE, 'role-permissions',
      ({ id, permissionKeys, expectedVersion }: { id: string; permissionKeys: string[]; expectedVersion: number }, options) =>
        adminClient.updateRolePermissions(id, permissionKeys, expectedVersion, options)),
  };
}
export function useAdminTenantMutations() {
  return useAdminMutation(PERMISSIONS.PLATFORM_ADMIN, 'update-tenant',
    ({ id, input }: Target<UpdateAdminTenantInput>, options) => adminClient.updateTenant(id, input, options));
}
